import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TabContext, TabPanel } from '@mui/lab';
import { BaseButton } from 'components/BaseButton';
import { ListingEntry } from 'graphql/listings/queries';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewBuyerSellerType, NewOrderPartyType } from 'types/order';
import ContactFormEdit from 'views/contacts/contacts/components/contact-form-edit/contact-form-edit';
import usePartyInfo from '../hooks/usePartyInfo';
import {
  formatRoleName,
  getInitials,
  normalizeRelations,
  uniqueByName,
  uniqueKinds,
} from '../utils';
import FormReporting from './FormReporting';
import PartyAssociationsList from './PartyAssociationsList';
import PartyCompaniesText from './PartyCompaniesText';
import PartyInfoSkeleton from './PartyInfoSkeleton';
import PartyModalChangeKinds from './PartyModalChangeKinds';
import PartySection from './PartySection';
import TabScrollButton from './TabScrollButton';

const useStyles = makeStyles((theme: any) => ({
  paper: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.border.card}`,
    borderRadius: 4,
    boxShadow: 'none',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
    },
  },
  back: {
    marginBottom: theme.spacing(1.5),
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': {
        minHeight: theme.spacing(5.5),
      },
    },
  },
  identity: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  identityInfo: {
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.dualText.primary,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 500,
  },
  name: {
    color: theme.palette.primary.darker,
    overflowWrap: 'anywhere',
  },
  companies: {
    marginTop: theme.spacing(0.5),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  chip: {
    height: 24,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.border.card,
    fontSize: theme.typography.caption.fontSize,
  },
  changeKinds: {
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': {
        minHeight: theme.spacing(5.5),
      },
    },
  },
  divider: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.divider,
  },
  tabs: {
    marginTop: theme.spacing(1),
    '& .MuiTab-root': {
      textTransform: 'none',
      [theme.breakpoints.up('sm')]: {
        minWidth: 0,
        maxWidth: 'none',
        padding: theme.spacing(0.75, 2),
      },
    },
  },
  panel: {
    padding: theme.spacing(2, 0, 0),
  },
  relation: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.neutraldivider}`,
    },
  },
  relationIcon: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  relationInfo: {
    minWidth: 0,
  },
  relationText: {
    color: theme.palette.primary.darker,
    overflowWrap: 'anywhere',
  },
  relationMeta: {
    color: theme.palette.text.secondary,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 0),
  },
  stateText: {
    color: theme.palette.primary.darker,
  },
  stateLabel: {
    color: theme.palette.text.secondary,
  },
}));

interface PartyInfoProps {
  partiesList: NewOrderPartyType[];
  listingEntries: ListingEntry[];
  handleFilterParties: (parties: NewOrderPartyType[]) => void;
  onBack: () => void;
  partiesLoading?: boolean;
}

const PartyInfo: FC<PartyInfoProps> = ({
  partiesList,
  listingEntries,
  handleFilterParties,
  onBack,
  partiesLoading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const {
    partyId,
    isLoading,
    isUpdating,
    isChangingKinds,
    handleUpdatePartyOrderData,
    partySelected,
    open,
    handleToggleModal,
    handleChangeKind,
    getPartyResponse,
  } = usePartyInfo(partiesList, handleFilterParties);

  const relations = normalizeRelations(partySelected?.member_of);
  const companyMembers = uniqueByName(relations);

  const showForm = Boolean(
    partySelected &&
      (partySelected.kinds?.includes('Buyer') || partySelected.kinds?.includes('Seller')) &&
      partySelected.order_data?.length > 0
  );

  const initialOrderData = partySelected?.order_data?.find(
    (data): data is NewBuyerSellerType => 'exception_1099' in data
  );

  const enableChangeKinds = partySelected
    ? listingEntries.filter((item) => partySelected.kinds.includes(item.code))
    : [];

  const [tabIndex, setTabIndex] = useState('order');

  const [previousPartyId, setPreviousPartyId] = useState(partyId);
  if (previousPartyId !== partyId) {
    setPreviousPartyId(partyId);
    setTabIndex('order');
  }

  const handleChange = (_, tab: string) => {
    setTabIndex(tab);
  };

  const renderInformation = () => {
    if (getPartyResponse.isLoading) {
      return (
        <Box className={classes.centered}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (getPartyResponse.isError) {
      return (
        <Box className={classes.state}>
          <Typography variant="body2" className={classes.stateText}>
            {t('parties:contact-load-error')}
          </Typography>
          <BaseButton
            buttonVariant="outlined"
            buttonColor="inherit"
            icon="sync"
            text={t('common:retry')}
            onClick={() => getPartyResponse.refetch()}
            customStyle={{ minHeight: 40 }}
          />
        </Box>
      );
    }

    if (getPartyResponse.data?.getParty) {
      return (
        <ContactFormEdit
          contact={getPartyResponse.data.getParty}
          refreshContacts={() => {
            getPartyResponse.refetch();
          }}
        />
      );
    }

    return null;
  };

  if (!partySelected && (partiesLoading || isLoading)) {
    return (
      <Paper variant="outlined" className={classes.paper}>
        <PartyInfoSkeleton />
      </Paper>
    );
  }

  if (!partySelected) {
    return (
      <Paper variant="outlined" className={classes.paper}>
        <Box className={classes.state}>
          <Typography variant="h6" component="h2" className={classes.stateText}>
            {t('parties:party-not-found')}
          </Typography>
          <Typography variant="body2" className={classes.stateLabel}>
            {t('parties:party-not-found-label')}
          </Typography>
          {!isDesktop && (
            <BaseButton
              buttonVariant="outlined"
              buttonColor="inherit"
              icon="arrow-left"
              text={t('parties:all-parties')}
              onClick={onBack}
              customStyle={{ minHeight: 40 }}
            />
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" className={classes.paper}>
      {!isDesktop && (
        <Box className={classes.back}>
          <BaseButton
            buttonVariant="text"
            buttonColor="inherit"
            icon="arrow-left"
            text={t('parties:all-parties')}
            onClick={onBack}
          />
        </Box>
      )}
      <Box className={classes.identity}>
        <Avatar className={classes.avatar}>{getInitials(partySelected.name)}</Avatar>
        <Box className={classes.identityInfo}>
          <Typography variant="h6" component="h2" className={classes.name}>
            {partySelected.name}
          </Typography>
          {companyMembers.length > 0 && (
            <Box className={classes.companies}>
              <PartyCompaniesText members={companyMembers} />
            </Box>
          )}
          <Box className={classes.chips}>
            {uniqueKinds(partySelected.kinds).map((kind) => (
              <Chip
                key={kind}
                size="small"
                variant="outlined"
                label={formatRoleName(kind)}
                className={classes.chip}
              />
            ))}
          </Box>
        </Box>
        {enableChangeKinds.length > 0 && (
          <Box className={classes.changeKinds}>
            <BaseButton
              buttonVariant="outlined"
              buttonColor="inherit"
              icon="pen"
              text={t('orders:changeContactKindsButton')}
              ariaLabel={t('parties:change-kinds-aria', { name: partySelected.name })}
              onClick={handleToggleModal}
              customStyle={{ minHeight: 40 }}
            />
          </Box>
        )}
      </Box>
      <Divider className={classes.divider} />
      <TabContext value={tabIndex}>
        <Tabs
          value={tabIndex}
          indicatorColor="secondary"
          textColor="secondary"
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          ScrollButtonComponent={TabScrollButton}
          className={classes.tabs}
          aria-label={t('parties:sections')}
        >
          <Tab value="order" label={t('orders:orderTab')} />
          <Tab value="information" label={t('orders:informationTab')} />
        </Tabs>
        <TabPanel value="order" className={classes.panel}>
          <Grid container spacing={2}>
            {showForm && (
              <Grid item xs={12} sm={6}>
                <PartySection title={t('parties:reporting-options')}>
                  <FormReporting
                    handleUpdatePartyOrderData={handleUpdatePartyOrderData}
                    initialValues={initialOrderData}
                    isSaving={isUpdating}
                  />
                </PartySection>
              </Grid>
            )}
            {relations.length > 0 && (
              <Grid item xs={12} sm={showForm ? 6 : 12}>
                <PartySection title={t('orders:relationsTitle')}>
                  {relations.map((member) => (
                    <Box key={`${member._id}-${member.kind}`} className={classes.relation}>
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        size="xs"
                        className={classes.relationIcon}
                      />
                      <Box className={classes.relationInfo}>
                        <Typography variant="body2" className={classes.relationText}>
                          {member.name}
                        </Typography>
                        <Typography variant="caption" className={classes.relationMeta}>
                          {formatRoleName(member.kind)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </PartySection>
              </Grid>
            )}
            <Grid item xs={12}>
              <PartyAssociationsList
                key={partySelected._id}
                associations={partySelected.associations}
                listingEntries={listingEntries}
                handleFilterParties={handleFilterParties}
                partySelected={partySelected}
              />
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value="information" className={classes.panel}>
          {renderInformation()}
        </TabPanel>
      </TabContext>
      {open && (
        <PartyModalChangeKinds
          open={open}
          listingEntries={listingEntries}
          handleCloseModal={handleToggleModal}
          enableChangeKinds={enableChangeKinds}
          handleChangeKind={handleChangeKind}
          isSubmitting={isChangingKinds}
        />
      )}
    </Paper>
  );
};

export default PartyInfo;
